import type { SerializedEditorState } from 'lexical';

import { LEGACY_FONT_MAP, PATTERN_FONTS, patternFontForStack } from '@/pattern-composer/fonts/pattern-fonts';
import { inferPatternLanguage } from '@/pattern-composer/model/pattern-language';
import type { PatternElement } from '@/pattern-composer/model/types';

import type { StoredPatternV2, StoredPatternV3 } from './stored-pattern';

const FONT_FAMILY = 'font-family';

/** Splits on ';' outside quotes, so a quoted family or url containing ';' stays whole. */
function splitDeclarations(style: string): string[] {
	const parts: string[] = [];
	let quote: string | null = null;
	let start = 0;
	for (let i = 0; i < style.length; i++) {
		const c = style[i]!;
		if (quote !== null) {
			if (c === '\\') i++;
			else if (c === quote) quote = null;
		} else if (c === '"' || c === "'") quote = c;
		else if (c === ';') {
			parts.push(style.slice(start, i));
			start = i + 1;
		}
	}
	parts.push(style.slice(start));
	return parts;
}

/** Rewrites the `font-family` declaration of one inline style string and nothing else: a pre-v3 menu value becomes
 *  its bundled stack, a bundled stack stays, any other family is dropped. Returns the input string when it has no
 *  `font-family` to change. */
export function rewriteStyleFonts(style: string): string {
	const declarations = splitDeclarations(style);
	let changed = false;
	const rewritten: string[] = [];
	for (const declaration of declarations) {
		const colon = declaration.indexOf(':');
		if (colon === -1 || declaration.slice(0, colon).trim().toLowerCase() !== FONT_FAMILY) {
			rewritten.push(declaration);
			continue;
		}
		const value = declaration.slice(colon + 1).trim();
		if (patternFontForStack(value) != null) {
			rewritten.push(declaration);
			continue;
		}
		changed = true;
		const mappedId = Object.hasOwn(LEGACY_FONT_MAP, value) ? LEGACY_FONT_MAP[value] : undefined;
		const mapped = PATTERN_FONTS.find((f) => f.id === mappedId);
		if (mapped != null) rewritten.push(`${FONT_FAMILY}: ${mapped.stack}`);
	}
	if (!changed) return style;
	return rewritten
		.map((d) => d.trim())
		.filter((d) => d !== '')
		.join('; ');
}

type LexicalNodeLike = Record<string, unknown>;

function rewriteNode(node: unknown): unknown {
	if (typeof node !== 'object' || node === null || Array.isArray(node)) return node;
	const n = node as LexicalNodeLike;
	let next: LexicalNodeLike = n;
	// Text nodes carry `style`; element nodes (paragraphs, list items) carry `textStyle`, which new text inherits.
	for (const key of ['style', 'textStyle'] as const) {
		const value = n[key];
		if (typeof value !== 'string') continue;
		const rewritten = rewriteStyleFonts(value);
		if (rewritten !== value) next = { ...next, [key]: rewritten };
	}
	if (Array.isArray(n.children)) {
		const children = n.children.map(rewriteNode);
		if (children.some((child, i) => child !== (n.children as unknown[])[i])) next = { ...next, children };
	}
	return next;
}

/** Rewrites the `font-family` of every styled node in a Lexical state. Copy-on-write: returns `state` itself when
 *  nothing changes. */
export function rewriteLegacyFonts(state: SerializedEditorState): SerializedEditorState {
	const root = rewriteNode(state.root);
	return root === state.root ? state : ({ ...state, root } as SerializedEditorState);
}

/** v2 -> v3: the document gains its language (inferred from its text) and every text box's fonts move to the
 *  bundled families. Everything else passes through unchanged. */
export function upgradeV2(v2: StoredPatternV2): StoredPatternV3 {
	const pages = v2.document.pages;
	return {
		...v2,
		formatVersion: 3,
		document: {
			setup: { ...v2.document.setup, language: inferPatternLanguage(pages) },
			pages: pages.map((page) => ({
				...page,
				elements: page.elements.map((el): PatternElement => {
					if (el.type !== 'text') return el;
					// No v2 app wrote `generated`; a stray one would be unvalidated here and fail the v3 parse after the next save.
					const { generated: _generated, ...text } = el;
					return text.editorState != null ? { ...text, editorState: rewriteLegacyFonts(text.editorState) } : text;
				}),
			})),
		},
	};
}
